import { Col, Row, Typography, Button, FormInstance, Space, Modal } from 'antd';
import React, { useState } from 'react';
import { useWatch } from 'antd/es/form/Form';
import ContentCard from '../../../../Common/ContentCard';

import styles from './index.module.less';
import { useAuth } from '../../../../../context/auth';

interface ICustomSection {
    form: FormInstance;
    label: string;
    description: string;
    children: React.ReactNode;
    controlsDisabled?: boolean;
    controlsLoading?: boolean;
    cardLoading?: boolean;
    editable?: boolean;
    cancelButtonLabel?: string;
    saveButtonLabel?: string;
    editButtonLabel?: string;
    isEditModeActive?: boolean;
    handleEditModeChange?: (value: boolean) => void;
}

const CustomSection = ({
    form,
    label,
    editable,
    description,
    children,
    controlsDisabled,
    controlsLoading,
    cardLoading,
    cancelButtonLabel,
    saveButtonLabel,
    editButtonLabel,
    isEditModeActive,
    handleEditModeChange,
}: ICustomSection) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { user } = useAuth();
  const email = useWatch('email');

  const emailChanged = user.email !== email;

    const handleCancel = () => {
        handleEditModeChange?.(false);
        form.resetFields();
    };

    const handleSave = () => {
      if (emailChanged) {
        toggleModal()
      }

      form.submit();
    };

    const handleEdit = () => {
        handleEditModeChange?.(true);
    };

    const toggleModal = () => {
      setIsModalVisible(prev => !prev);
    }

    return (
        <section>
            <Row gutter={[20, 20]}>
                <Col xl={8} span={24}>
                    <div className={styles.textBox}>
                        <Typography.Title level={4} className={styles.label}>
                            {label}
                        </Typography.Title>
                        <Typography.Text className={styles.description}>{description}</Typography.Text>
                    </div>
                </Col>
                <Col xl={16} span={24}>
                    <ContentCard
                        bordered
                        padding={20}
                    >
                        <Row justify='end'>
                            {editable &&
                                (isEditModeActive && !controlsDisabled ? (
                                    <Space size={4}>
                                        <Button
                                            type="default"
                                            size="large"
                                            onClick={handleCancel}
                                            disabled={controlsLoading}
                                            loading={controlsLoading}
                                        >
                                            {cancelButtonLabel}
                                        </Button>
                                        <Button
                                            type="primary"
                                            size="large"
                                            onClick={handleSave}
                                            disabled={controlsLoading}
                                            loading={controlsLoading}
                                        >
                                            {saveButtonLabel}
                                        </Button>
                                    </Space>
                                ) : (
                                    <Button
                                        type="primary"
                                        size="large"
                                        onClick={handleEdit}
                                        disabled={controlsDisabled || cardLoading}
                                    >
                                        {editButtonLabel}
                                    </Button>
                                ))}
                        </Row>
                        <ContentCard loading={!!cardLoading}>{children}</ContentCard>
                    </ContentCard>
                </Col>
            </Row>
          <Modal
            title="Email Verification"
            open={isModalVisible}
            onOk={toggleModal}
            onCancel={toggleModal}
            okText="Got it"
            cancelButtonProps={{ style: { display: 'none' } }}
          >
            <Typography.Paragraph>
              We've sent a verification email to your new email address.
            </Typography.Paragraph>
            <Typography.Paragraph>
              To complete your email update:
            </Typography.Paragraph>
            <ol style={{ listStyleType: 'disc', marginLeft: '20px' }}>
              <li>Check your inbox</li>
              <li>Click the verification link in the email</li>
              <li>Your email will be updated after verification</li>
            </ol>
            <Typography.Text type='warning'>
              Note: Your email change will only take effect after verification.
            </Typography.Text>
          </Modal>
        </section>
    );
};

CustomSection.defaultProps = {
    cardLoading: false,
    controlsLoading: false,
    controlsDisabled: false,
    editable: false,
    cancelButtonLabel: 'Cancel',
    saveButtonLabel: 'Save',
    editButtonLabel: 'Edit',
    isEditModeActive: false,
    handleEditModeChange: undefined,
};

export default CustomSection;
